import { Component, DestroyRef, Inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DepartmentsDTO, DepartmentSelectionResult } from '@core/models';
import { CommonDepartmentsDataService, UserService } from '@core/services';
import { CustomSortingTableDataSource } from '@core/utils';
import { BehaviorSubject, map, startWith, Subscription, switchMap, tap } from 'rxjs';
import { CommonDialogsService } from '../../services/common-dialogs/common-dialogs.service';

@Component({
  selector: 'app-dialog-select-department',
  templateUrl: './dialog-select-department.component.html',
  styleUrl: './dialog-select-department.component.scss',
  standalone: false,
})
export class DialogSelectDepartmentComponent {
  private dialogRefSubscription!: Subscription;

  isLoading = signal<boolean>(false);

  form!: FormGroup;
  formFilter = new FormControl('');

  public departments$ = new BehaviorSubject<DepartmentsDTO[]>([]);
  private allDepartments: DepartmentsDTO[] = [];

  get allDepartmentField(): FormControl {
    return this.form.get('selectAll') as FormControl;
  }

  get departmentField(): FormGroup {
    return this.form.get('departments') as FormGroup;
  }

  get isUserHaveAccessToAllDepartments(): boolean | undefined {
    return this.userService.isUserHaveAccessToAllDepartments;
  }

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private readonly destroyRef: DestroyRef,
    private dialogRef: MatDialogRef<DialogSelectDepartmentComponent>,
    private readonly commonDialogsService: CommonDialogsService,
    private readonly commonDepartmentsDataService: CommonDepartmentsDataService,
    @Inject(MAT_DIALOG_DATA) public readonly data: DepartmentSelectionResult | null,
  ) {}

  ngOnInit(): void {
    this.setupDepartmentsList();
    this.setupBackdropClickHandler();
  }

  private setupDepartmentsList(): void {
    this.isLoading.set(true);

    this.commonDepartmentsDataService
      .getDepartments()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((departments) => {
          this.allDepartments = departments;
          this.initForm(departments);
          this.setupSelectAllListener();
          this.isLoading.set(false);
        }),
        switchMap((data) => {
          const departments = new CustomSortingTableDataSource<DepartmentsDTO>(data);

          departments.filterPredicate = (department: DepartmentsDTO, filter: string) => {
            const filterText = filter.trim().toLowerCase();
            return department.name.toLowerCase().includes(filterText);
          };

          return this.formFilter.valueChanges.pipe(
            startWith(''),
            map((filter: string | null) => {
              departments.filter = (filter || '').trim().toLowerCase();
              return departments.filteredData;
            }),
          );
        }),
      )
      .subscribe((filteredData) => {
        this.departments$.next(filteredData);
      });
  }

  private initForm(departments: DepartmentsDTO[]): void {
    const departmentsGroup = this.fb.group({});
    const userDepartments = this.userService.listUserDepartments || [];
    const hasAllDepsAccess = this.isUserHaveAccessToAllDepartments;
    const wasAllDepsSelected = this.data?.all_departments_access || false;

    departments.forEach((department) => {
      const isPreSelected = this.data?.departments?.some((selectedDept: any) => selectedDept.id === department.id);
      const isUserDepartment = userDepartments.some((userDept: any) => userDept.id === department.id);

      const control = new FormControl(isPreSelected || false);

      if (wasAllDepsSelected || (!hasAllDepsAccess && !isUserDepartment)) {
        control.disable({ emitEvent: false });
      }

      departmentsGroup.addControl(department.id.toString(), control);
    });

    const selectAllControl = new FormControl(wasAllDepsSelected);
    if (!hasAllDepsAccess) {
      selectAllControl.disable({ emitEvent: false });
    }

    this.form = this.fb.group({
      departments: departmentsGroup,
      selectAll: selectAllControl,
    });
  }

  private setupBackdropClickHandler(): void {
    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setCloseConfirmation();
      });
  }

  private setupSelectAllListener(): void {
    this.allDepartmentField.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((isAllSelected) => {
          return this.departments$.pipe(map((departments) => ({ isAllSelected, departments })));
        }),
      )
      .subscribe(({ isAllSelected, departments }) => {
        departments.forEach((department) => {
          const control = this.departmentField.get(department.id.toString());
          if (control) {
            if (isAllSelected) {
              control.disable({ emitEvent: false });
            } else {
              control.enable({ emitEvent: false });
            }
          }
        });
        this.form.markAsDirty();
      });
  }

  setCloseConfirmation(): void {
    if (this.form?.dirty && this.canSubmitForm()) {
      this.dialogRefSubscription?.unsubscribe();
      this.dialogRefSubscription = this.commonDialogsService.closeConfirm().subscribe((res) => {
        if (res) {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  canSubmitForm(): boolean {
    if (!this.form?.valid) return false;

    const allDepartmentsChanged = this.allDepartmentField.value !== (this.data?.all_departments_access || false);

    if (allDepartmentsChanged) {
      return true;
    }

    const selected = Object.entries(this.departmentField.getRawValue())
      .filter(([, value]) => value)
      .map((value) => Number(value[0]));

    const initial = this.data?.departments.map((d) => d.id) || [];

    return JSON.stringify(selected.sort()) !== JSON.stringify(initial.sort());
  }

  onSubmit(): void {
    if (!this.form.valid) return;

    if (this.allDepartmentField.value) {
      // All departments selected case
      this.dialogRef.close({
        all_departments_access: true,
        departments: [],
      });
      return;
    }

    // Get selected departments
    const departments = this.allDepartments;
    const selectedDepartments = departments
      .filter((department) => {
        const control = this.departmentField.get(department.id.toString());
        return control?.value === true;
      })
      .map((department) => ({
        id: department.id,
        name: department.name,
      }));

    this.dialogRef.close({
      all_departments_access: false,
      departments: selectedDepartments,
    });
  }

  ngOnDestroy(): void {
    this.dialogRefSubscription?.unsubscribe();
  }
}
